import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import merge from 'lodash/merge';
import set from 'lodash/set';
import sumBy from 'lodash/sumBy';
import classnames from 'classnames';
import BeneficiaryForm from '../BeneficiaryForm';
import ModalBeneficiaryDetails from '../ModalBeneficiaryDetails';
import ModalConfirmation from '../ModalConfirmation';
import * as analytics from '../../services/analytics';
import './Passport360EnrollmentBeneficiariesView.css';

const propTypes = {
  client: PropTypes.object,
  onNotifyError: PropTypes.func,
  onNotifySuccess: PropTypes.func,
  onSubmit: PropTypes.func,
  quote: PropTypes.object,
  updateClient: PropTypes.func,
  updateQuote: PropTypes.func,
};

class Passport360EnrollmentBeneficiariesView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      beneficiaries: get(props.quote, 'application.beneficiaries'),
      beneficiaryIndex: 0,
      editingBeneficiary: {},
      hasAllotmentError: false,
    };
  }

  componentDidMount() {
    analytics.page('Passport 360 - Enrollment Add Beneficiaries');
  }

  handleUpdateDetails = (e) => {
    const updatedBeneficiary = {
      ...this.state.editingBeneficiary,
    };
    set(updatedBeneficiary, e.target.name, e.target.value);
    this.setState({
      editingBeneficiary: merge(
        {},
        this.state.editingBeneficiary,
        updatedBeneficiary
      ),
    });
  };

  handleUpdateAllotment = (e) => {
    analytics.track('Passport 360 - Beneficiary Allotment Updated');
    const application = {
      beneficiaries: [...this.state.beneficiaries],
    };
    set(application, e.target.name, e.target.value);
    const percentageSum = sumBy(application.beneficiaries, (beneficiary) =>
      parseInt(beneficiary.percentage, 10)
    );
    this.setState({
      beneficiaries: application.beneficiaries,
      hasAllotmentError: percentageSum !== 100,
    });
  };

  commitBeneficiary = (e) => {
    if (e) {
      e.preventDefault();
    }
    const beneficiaries = [...this.state.beneficiaries];
    beneficiaries[parseInt(this.state.beneficiaryIndex, 10)] = {
      ...this.state.editingBeneficiary,
      address: {
        ...this.state.editingBeneficiary.address,
        // XXX: Hardcoding country until Google Maps Autocomplete implemented
        country: {
          code: 'US',
          name: 'United States',
        },
      },
      percentage: '100',
    };

    if (beneficiaries.length > 1) {
      let sum = 0;
      beneficiaries.forEach((beneficiary, i) => {
        const percentage = Math.round(100 / beneficiaries.length);
        sum += percentage;
        beneficiary.percentage = percentage;
        if (i === beneficiaries.length - 1) {
          beneficiary.percentage += 100 - sum;
        }
      });
    }

    analytics.track('Passport 360 - Add Beneficiary Submitted', {
      value: beneficiaries.length,
    });
    this.props
      .updateQuote(
        merge({}, this.props.quote, {
          application: {
            beneficiaries,
          },
        })
      )
      .then(() => {
        analytics.track('Passport 360 - Add Beneficiary Succeeded', {
          value: beneficiaries.length,
        });
        this.setState({
          beneficiaries,
          editingBeneficiary: {},
        });
        this.beneficiaryModal.hide();
      })
      .catch((error) => {
        analytics.track('Passport 360 - Add Beneficiary Failed', {
          value: beneficiaries.length,
        });
        this.props.onNotifyError(error);
      });
  };

  handleNext = (e) => {
    if (e) {
      e.preventDefault();
    }
    this.props
      .updateQuote(
        merge({}, this.props.quote, {
          application: {
            beneficiaries: this.state.beneficiaries,
          },
        })
      )
      .then(this.props.onSubmit)
      .catch(this.props.onNotifyError);
  };

  showAddBeneficiaryModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    analytics.track('Passport 360 - Add Beneficiary Modal Opened', {
      value: this.state.beneficiaries.length,
    });
    this.setState(
      {
        beneficiaryIndex: this.state.beneficiaries.length,
        editingBeneficiary: {},
      },
      () => {
        this.beneficiaryModal.show();
      }
    );
  };

  showEditBeneficiaryModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    const index = parseInt(e.target.dataset.beneficiaryIndex, 10);
    const beneficiary = this.state.beneficiaries[index];
    analytics.track('Passport 360 - Edit Beneficiary Modal Opened', {
      value: index,
    });
    this.setState(
      {
        beneficiaryIndex: index,
        editingBeneficiary: {
          ...beneficiary,
        },
      },
      () => {
        this.beneficiaryModal.show();
      }
    );
  };

  showRemoveBeneficiaryModal = (e) => {
    e.preventDefault();
    const index = parseInt(e.target.dataset.beneficiaryIndex, 10);
    const beneficiary = this.state.beneficiaries[index];
    analytics.track('Passport 360 - Remove Beneficiary Modal Opened', {
      value: index,
    });
    this.setState(
      {
        beneficiaryIndex: index,
        editingBeneficiary: {
          ...beneficiary,
        },
      },
      () => {
        this.removeBeneficiaryModal.show();
      }
    );
  };

  removeBeneficiary = () => {
    const beneficiaries = [...this.state.beneficiaries];
    beneficiaries.splice(parseInt(this.state.beneficiaryIndex, 10), 1);

    if (beneficiaries.length > 1) {
      let sum = 0;
      beneficiaries.forEach((beneficiary, i) => {
        const percentage = Math.round(100 / beneficiaries.length);
        sum += percentage;
        beneficiary.percentage = percentage;
        if (i === beneficiaries.length - 1) {
          beneficiary.percentage += 100 - sum;
        }
      });
    } else if (beneficiaries.length) {
      beneficiaries[0].percentage = '100';
    }

    const application = {
      ...this.props.quote.application,
      beneficiaries,
    };

    analytics.track('Passport 360 - Remove Beneficiary Submitted', {
      value: beneficiaries.length,
    });
    this.props
      .updateQuote({
        ...this.props.quote,
        application,
      })
      .then(() => {
        analytics.track('Passport 360 - Remove Beneficiary Succeeded', {
          value: beneficiaries.length,
        });
        this.setState({
          beneficiaries,
          editingBeneficiary: {},
          beneficiaryIndex: 0,
        });
        this.removeBeneficiaryModal.hide();
      })
      .catch((error) => {
        analytics.track('Passport 360 - Remove Beneficiary Failed', {
          value: beneficiaries.length,
        });
        this.props.onNotifyError(error);
      });
  };

  get title() {
    const { actor, client } = this.props;
    if (actor === 'broker') {
      return `Tell us about ${client.person.first_name}'s beneficiaries:`;
    } else {
      return 'Tell us about your beneficiaries';
    }
  }

  render() {
    const { client } = this.props;
    const { beneficiaries, editingBeneficiary, hasAllotmentError } = this.state;

    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-7 p-t-30">
            <h3 className="text-center semi-bold">{this.title}</h3>

            {!!beneficiaries.length && (
              <Fragment>
                <form onSubmit={this.commitBeneficiary}>
                  {beneficiaries.map((beneficiary, i) => (
                    <div
                      className="Passport360EnrollmentBeneficiariesView-beneficiaryCard card m-t-20"
                      key={beneficiary.email}
                    >
                      <div className="card-block p-l-30 p-r-30">
                        <div
                          className={classnames('form-group row', {
                            'has-error': hasAllotmentError,
                          })}
                        >
                          <label className="col-md-3 text-uppercase">
                            Beneficiary {i + 1}
                          </label>
                          <div className="col-md-5">
                            <p>
                              <strong>
                                {beneficiary.first_name} {beneficiary.last_name}
                              </strong>
                              <br />
                              Email: {beneficiary.email}
                              <br />
                              Phone: {beneficiary.phone_number}
                            </p>
                          </div>
                          <div className="col-md-3">
                            <label
                              htmlFor={`beneficiaryAllotment${i}`}
                              className="text-uppercase"
                            >
                              Allotment
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id={`beneficiaryAllotment${i}`}
                                name={`beneficiaries[${i}].percentage`}
                                value={
                                  parseInt(beneficiaries[i].percentage, 10) ||
                                  ''
                                }
                                pattern="\d+"
                                aria-required="true"
                                required
                                onChange={this.handleUpdateAllotment}
                              />
                              <span className="input-group-addon">%</span>
                            </div>
                            {hasAllotmentError && (
                              <div className="form-control-feedback">
                                Allotments must add up to 100%
                              </div>
                            )}
                          </div>
                          <div className="col-md-1">
                            <div className="dropdown">
                              <button
                                className="Passport360EnrollmentBeneficiariesView-editDropdownBtn btn btn-secondary dropdown-toggle"
                                type="button"
                                id={`editBeneficiary${i}Dropdown`}
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                              >
                                Edit
                              </button>
                              <div
                                className="dropdown-menu"
                                aria-labelledby={`editBeneficiary${i}Dropdown`}
                              >
                                <a
                                  href="#edit-beneficiary"
                                  className="dropdown-item text-complete"
                                  data-beneficiary-index={i}
                                  style={{ cursor: 'pointer' }}
                                  onClick={this.showEditBeneficiaryModal}
                                >
                                  Edit
                                </a>
                                <a
                                  href="#remove-beneficiary"
                                  className="dropdown-item text-danger"
                                  style={{ cursor: 'pointer' }}
                                  data-beneficiary-index={i}
                                  onClick={this.showRemoveBeneficiaryModal}
                                >
                                  Remove
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </form>
                <button
                  className="btn btn-block btn-secondary m-b-20"
                  onClick={this.showAddBeneficiaryModal}
                >
                  <i className="far fa-plus" /> Add another beneficiary
                </button>
                <div className="row justify-content-center">
                  <button
                    className="btn btn-lg btn-complete"
                    disabled={hasAllotmentError}
                    onClick={this.handleNext}
                  >
                    Next
                  </button>
                </div>
              </Fragment>
            )}

            {!beneficiaries.length && (
              <form onSubmit={this.commitBeneficiary}>
                <div className="Passport360EnrollmentBeneficiariesView-clientDetails card m-t-20">
                  <div className="card-block p-l-30 p-r-30">
                    <BeneficiaryForm
                      client={client}
                      beneficiary={editingBeneficiary}
                      onChange={this.handleUpdateDetails}
                    />
                  </div>
                </div>
                <div className="row justify-content-center">
                  <button className="btn btn-lg btn-complete">Next</button>
                </div>
              </form>
            )}
          </div>
        </div>

        <ModalBeneficiaryDetails
          client={client}
          beneficiary={editingBeneficiary}
          onChange={this.handleUpdateDetails}
          onSubmit={this.commitBeneficiary}
          ref={(c) => (this.beneficiaryModal = c)}
        />

        <ModalConfirmation
          title="Remove beneficiary"
          confirmCta="Remove"
          onConfirm={this.removeBeneficiary}
          ref={(c) => (this.removeBeneficiaryModal = c)}
        >
          Are you sure you want to remove {editingBeneficiary.first_name}{' '}
          {editingBeneficiary.last_name}?
        </ModalConfirmation>
      </div>
    );
  }
}

Passport360EnrollmentBeneficiariesView.propTypes = propTypes;

export default Passport360EnrollmentBeneficiariesView;
